/**
 * MarketRibbon — stock-exchange style horizontal ticker scrolling through
 * material/labour rates for the selected region. Shows up/down delta
 * (deterministic per-item, simulated daily delta).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ConstructionAPI from './api';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';

// Deterministic pseudo-delta in [-3.5, +4.0]% based on item code & today's date
function deltaFor(item, dayKey) {
  const seed = (item.item_code + dayKey).split('').reduce((s, c) => (s * 31 + c.charCodeAt(0)) | 0, 17);
  const r = Math.abs((Math.sin(seed) + 1) / 2);            // 0..1
  return ((r * 7.5) - 3.5);                                 // -3.5 .. +4.0
}

export default function MarketRibbon({ region = 'us' }) {
  const [items, setItems] = useState([]);
  const containerRef = useRef(null);
  const dayKey = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      ConstructionAPI.getMaterials(region),
      ConstructionAPI.getLabour(region),
    ]).then(([mat, lab]) => {
      if (cancelled) return;
      const m = (mat.items || []).slice(0, 12).map(i => ({ ...i, kind: 'mat' }));
      const l = (lab.items || []).slice(0, 8).map(i => ({ ...i, kind: 'lab' }));
      setItems([...m, ...l]);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [region]);

  if (items.length === 0) return null;

  const tickers = items.map(it => ({
    code: (it.item_code || '').toUpperCase().slice(0, 10),
    label: (it.description || '').slice(0, 28),
    rate: it.regional_rate || it.usd_rate,
    unit: it.unit,
    kind: it.kind,
    delta: deltaFor(it, dayKey),
  }));

  // Duplicate items to make the marquee seamless
  const all = [...tickers, ...tickers];

  return (
    <div className="market-ribbon" data-testid="market-ribbon">
      <div className="market-ribbon-pill">
        <Activity className="h-3.5 w-3.5" />
        <span className="font-semibold tracking-wide">LIVE · {region.toUpperCase()}</span>
      </div>
      <div className="market-ribbon-track" ref={containerRef}>
        <div className="market-ribbon-scroll">
          {all.map((t, i) => (
            <div key={i} className="market-ribbon-item" data-testid={i < tickers.length ? `ticker-${t.code}` : undefined}>
              <span className={`mr-code ${t.kind === 'lab' ? 'mr-lab' : 'mr-mat'}`}>{t.code}</span>
              <span className="mr-label">{t.label}</span>
              <span className="mr-rate">${Number(t.rate || 0).toLocaleString()}/{t.unit}</span>
              <span className={`mr-delta ${t.delta >= 0 ? 'mr-up' : 'mr-down'}`}>
                {t.delta >= 0
                  ? <TrendingUp className="h-3 w-3 inline" />
                  : <TrendingDown className="h-3 w-3 inline" />}
                {' '}{t.delta >= 0 ? '+' : ''}{t.delta.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
